<script lang="ts">
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Archive, CheckSquare, ChevronDown, ChevronLeft, ChevronRight, Pencil, Tags, X } from 'lucide-svelte';
  import { catalogApi } from '$lib/api/catalog';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { minorToInputValue, parseMoneyToMinor } from '$lib/utils/money';
  import ProductCategoryBadges from '$lib/components/ui/ProductCategoryBadges.svelte';

  const {
    category,
    family,
    ownership,
    missingTaxonomy,
    missingPrice,
    active,
    q,
    source = 'products',
    months
  }: {
    category?: string;
    family?: string;
    ownership?: string;
    missingTaxonomy?: boolean;
    missingPrice?: boolean;
    active?: boolean;
    q?: string;
    // 'review'/'stale' swap the data source to the matching hygiene
    // endpoint but reuse this list's rendering/inline-edit/bulk-action
    // affordances wholesale — see catalogApi.review/stale.
    source?: 'products' | 'review' | 'stale';
    months?: number; // 'stale' only — window override, see catalogApi.stale
  } = $props();

  const queryClient = useQueryClient();
  let page = $state(1);
  let selected = $state<Set<number>>(new Set());
  let expanded = $state<Set<number>>(new Set());

  // Reset paging/selection whenever the caller hands us a different filter
  // (drilling into a new category, clicking a different health stat, or
  // typing a new search term) — a stale page/selection from the previous
  // view is confusing, not helpful.
  $effect(() => {
    void category;
    void family;
    void ownership;
    void missingTaxonomy;
    void missingPrice;
    void active;
    void q;
    void source;
    void months;
    page = 1;
    selected = new Set();
    expanded = new Set();
  });

  // review/stale return the full unpaginated set (see their handler doc
  // comments) — only the plain product list paginates.
  const productsQuery = createQuery(() => ({
    queryKey: ['catalog-products', { category, family, ownership, missingTaxonomy, missingPrice, active, q, page, source, months }],
    queryFn: () => {
      if (source === 'review') return catalogApi.review();
      if (source === 'stale') return catalogApi.stale(months);
      return catalogApi.products({
        category,
        family,
        ownership,
        missing_taxonomy: missingTaxonomy || undefined,
        missing_price: missingPrice || undefined,
        active,
        q: q || undefined,
        page,
        per_page: 50
      });
    }
  }));

  const categoriesQuery = createQuery(() => ({
    queryKey: ['catalog-categories'],
    queryFn: () => catalogApi.categories()
  }));

  const rows = $derived(productsQuery.data?.results ?? []);
  // review/stale return their full result set unpaginated — no pager.
  const totalPages = $derived(
    source === 'products' ? Math.max(1, Math.ceil((productsQuery.data?.count ?? 0) / 50)) : 1
  );
  const allSelectedInView = $derived(rows.length > 0 && rows.every((r) => selected.has(r.id)));

  function toggleOne(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selected = next;
  }

  function toggleAllInView() {
    if (allSelectedInView) {
      selected = new Set();
    } else {
      selected = new Set(rows.map((r) => r.id));
    }
  }

  function toggleDetails(id: number) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    expanded = next;
  }

  function invalidateAfterMutation() {
    queryClient.invalidateQueries({ queryKey: ['catalog-products'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-categories'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-health'] });
  }

  // --- single-row inline edit: click a field, change it, save on blur/change ---

  const inlineEditMutation = createMutation(() => ({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => catalogApi.updateProduct(id, body),
    onSuccess: invalidateAfterMutation,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function saveField(id: number, field: string, value: unknown) {
    inlineEditMutation.mutate({ id, body: { [field]: value } });
  }

  // --- bulk actions ---

  let bulkCategoryValue = $state('');
  let bulkOwnershipValue = $state('');
  let bulkFuncCategory = $state('');
  let bulkFuncType = $state('');
  let bulkAction = $state<'category' | 'ownership' | 'taxonomy' | 'rcu' | 'archive'>('category');

  const bulkCategoryMutation = createMutation(() => ({
    mutationFn: () => catalogApi.bulkCategory([...selected], bulkCategoryValue.trim()),
    onSuccess: (data) => {
      invalidateAfterMutation();
      toast.success(`Moved ${data.moved} product${data.moved === 1 ? '' : 's'}.`);
      selected = new Set();
      bulkCategoryValue = '';
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const bulkOwnershipMutation = createMutation(() => ({
    mutationFn: () => catalogApi.bulkOwnership([...selected], bulkOwnershipValue || null),
    onSuccess: (data) => {
      invalidateAfterMutation();
      toast.success(`Updated ownership on ${data.moved} product${data.moved === 1 ? '' : 's'}.`);
      selected = new Set();
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const bulkRCUMutation = createMutation(() => ({
    mutationFn: (syncs: boolean) => catalogApi.bulkRCU([...selected], syncs),
    onSuccess: (data) => {
      invalidateAfterMutation();
      toast.success(`Updated RCU sync on ${data.moved} product${data.moved === 1 ? '' : 's'}.`);
      selected = new Set();
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  // Stale view only: archive the checked products outright (see
  // catalogApi.bulkArchive) — explicit selection, not the whole stale scope.
  const bulkArchiveMutation = createMutation(() => ({
    mutationFn: () => catalogApi.bulkArchive([...selected]),
    onSuccess: (data) => {
      invalidateAfterMutation();
      toast.success(`Archived ${data.moved} product${data.moved === 1 ? '' : 's'}.`);
      selected = new Set();
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const bulkTaxonomyMutation = createMutation(() => ({
    mutationFn: () =>
      catalogApi.bulkTaxonomy([...selected], {
        functional_category: bulkFuncCategory.trim(),
        functional_type: bulkFuncType.trim()
      }),
    onSuccess: (data) => {
      invalidateAfterMutation();
      toast.success(`Updated RCU taxonomy on ${data.moved} product${data.moved === 1 ? '' : 's'}.`);
      selected = new Set();
      bulkFuncCategory = '';
      bulkFuncType = '';
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));
</script>

<datalist id="catalog-category-options">
  {#each categoriesQuery.data?.results ?? [] as c (c.category)}
    {#if c.category}<option value={c.category}></option>{/if}
  {/each}
</datalist>

{#if selected.size > 0}
  <div class="tym-card mb-3 overflow-hidden border-toyama-teal/30" style="background:var(--tym-teal-100)">
    <div class="flex flex-wrap items-center gap-2 border-b border-toyama-teal/15 px-3.5 py-2.5">
      <span class="flex items-center gap-2 text-[13px] font-semibold text-navy"><CheckSquare class="h-4 w-4 text-toyama-teal" />{selected.size} selected</span>
      <span class="hidden text-[12px] text-dark-gray sm:inline">Choose one action to apply across the selection.</span>
      <button type="button" class="ml-auto inline-flex h-7 items-center gap-1 rounded-md px-2 text-[12px] font-medium text-dark-gray hover:bg-white/60 hover:text-navy" onclick={() => (selected = new Set())}><X class="h-3.5 w-3.5" /> Clear</button>
    </div>
    <div class="flex flex-col gap-3 p-3.5 sm:flex-row sm:items-end">
      <div class="min-w-[180px]">
        <label class="label !mb-1" for="bulk-action">Bulk action</label>
        <select id="bulk-action" class="input !h-9" bind:value={bulkAction}>
          <option value="category">Move to category</option>
          <option value="ownership">Set ownership</option>
          <option value="taxonomy">Set RCU taxonomy</option>
          <option value="rcu">Set RCU sync</option>
          {#if source === 'stale'}<option value="archive">Archive selected</option>{/if}
        </select>
      </div>
      {#if bulkAction === 'category'}
        <div class="min-w-0 flex-1 sm:max-w-sm"><label class="label !mb-1" for="bulk-category">Destination category</label><input id="bulk-category" class="input !h-9" list="catalog-category-options" placeholder="Start typing a category…" bind:value={bulkCategoryValue} /></div>
        <button type="button" class="btn-primary !h-9" disabled={bulkCategoryMutation.isPending || !bulkCategoryValue.trim()} onclick={() => bulkCategoryMutation.mutate()}>{bulkCategoryMutation.isPending ? 'Moving…' : 'Move products'}</button>
      {:else if bulkAction === 'ownership'}
        <div class="min-w-[180px]"><label class="label !mb-1" for="bulk-ownership">New ownership</label><select id="bulk-ownership" class="input !h-9" bind:value={bulkOwnershipValue}><option value="">No ownership</option><option value="grms">GRMS</option><option value="load">Load</option><option value="accessory">Accessory</option></select></div>
        <button type="button" class="btn-primary !h-9" disabled={bulkOwnershipMutation.isPending} onclick={() => bulkOwnershipMutation.mutate()}>{bulkOwnershipMutation.isPending ? 'Saving…' : 'Apply ownership'}</button>
      {:else if bulkAction === 'taxonomy'}
        <div class="min-w-0 flex-1"><label class="label !mb-1" for="bulk-functional-category">Functional category</label><input id="bulk-functional-category" class="input !h-9" placeholder="e.g. lighting" bind:value={bulkFuncCategory} /></div>
        <div class="min-w-0 flex-1"><label class="label !mb-1" for="bulk-functional-type">Functional type</label><input id="bulk-functional-type" class="input !h-9" placeholder="e.g. downlight" bind:value={bulkFuncType} /></div>
        <button type="button" class="btn-primary !h-9" disabled={bulkTaxonomyMutation.isPending || (!bulkFuncCategory.trim() && !bulkFuncType.trim())} onclick={() => bulkTaxonomyMutation.mutate()}>{bulkTaxonomyMutation.isPending ? 'Saving…' : 'Apply taxonomy'}</button>
      {:else if bulkAction === 'rcu'}
        <p class="flex-1 text-[12.5px] text-dark-gray">Enable sync only after the selected products have an RCU category and type.</p>
        <button type="button" class="btn-secondary !h-9" disabled={bulkRCUMutation.isPending} onclick={() => bulkRCUMutation.mutate(false)}>Disable sync</button>
        <button type="button" class="btn-primary !h-9" disabled={bulkRCUMutation.isPending} onclick={() => bulkRCUMutation.mutate(true)}>{bulkRCUMutation.isPending ? 'Saving…' : 'Enable sync'}</button>
      {:else if bulkAction === 'archive'}
        <p class="flex-1 text-[12.5px] text-dark-gray">Archived products stay in historic quotations but are no longer available for new ones.</p>
        <button type="button" class="btn-danger !h-9" disabled={bulkArchiveMutation.isPending} onclick={() => bulkArchiveMutation.mutate()}><Archive class="h-4 w-4" />{bulkArchiveMutation.isPending ? 'Archiving…' : 'Archive selected'}</button>
      {/if}
    </div>
  </div>
{/if}

{#if productsQuery.isLoading}
  <p class="tym-loading-state">Loading products&hellip;</p>
{:else if productsQuery.isError}
  <p class="tym-error-state">{getErrorMessage(productsQuery.error)}</p>
{:else}
  <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
    <p class="tym-small"><span class="font-semibold text-navy">{productsQuery.data?.count ?? rows.length}</span> product{(productsQuery.data?.count ?? rows.length) === 1 ? '' : 's'} in this view</p>
    <p class="flex items-center gap-1.5 text-[11.5px] text-dark-gray"><Tags class="h-3.5 w-3.5" /> Open Details to edit taxonomy, ownership and pricing.</p>
  </div>
  <div class="tym-table-wrap tym-table-scroll border border-toyama-cloudy-gray shadow-[var(--tym-shadow-1)]">
    <table class="tym-table">
      <thead>
        <tr>
          <th class="w-[34px]">
            <input type="checkbox" checked={allSelectedInView} onchange={toggleAllInView} aria-label="Select all in view" />
          </th>
          <th>Product</th>
          <th class="w-[190px]">Category</th>
          <th class="num w-[100px]">List price</th>
          <th class="w-[82px]">Status</th>
          <th class="w-[90px]"><span class="sr-only">Edit details</span></th>
        </tr>
      </thead>
      <tbody>
        {#each rows as p (p.id)}
          <tr>
            <td><input type="checkbox" checked={selected.has(p.id)} onchange={() => toggleOne(p.id)} aria-label="Select {p.name}" /></td>
            <td>
              <div class="text-[13px] font-medium text-navy leading-tight">{p.name}</div>
              <div class="text-[11px] text-dark-gray font-mono mt-0.5">{p.part_number || 'No part number'}</div>
            </td>
            <td><ProductCategoryBadges product={p} /></td>
            <td class="num">
              {p.list_price_minor === null ? '—' : minorToInputValue(p.list_price_minor)}
            </td>
            <td>
              <span class="tym-badge {p.active ? 'tym-badge-success' : 'tym-badge-neutral'} !text-[10px]">{p.active ? 'Active' : 'Inactive'}</span>
            </td>
            <td><button type="button" class="btn-secondary !h-7 !px-2 !text-xs" onclick={() => toggleDetails(p.id)} aria-expanded={expanded.has(p.id)}><Pencil class="h-3 w-3" /> Details <ChevronDown class="h-3 w-3" /></button></td>
          </tr>
          {#if expanded.has(p.id)}
            <tr class="bg-toyama-light-carbon"><td></td><td colspan="5"><div class="grid gap-3 py-2 sm:grid-cols-2 lg:grid-cols-5">
              <div><label class="label !mb-1" for="edit-category-{p.id}">Category</label><input id="edit-category-{p.id}" class="input !h-8 !text-xs" list="catalog-category-options" value={p.category} onchange={(e) => saveField(p.id, 'category', (e.currentTarget as HTMLInputElement).value)} /></div>
              <div><label class="label !mb-1" for="edit-ownership-{p.id}">Ownership</label><select id="edit-ownership-{p.id}" class="input !h-8 !text-xs" value={p.ownership ?? ''} onchange={(e) => { const v = (e.currentTarget as HTMLSelectElement).value; saveField(p.id, 'ownership', v || null); }}><option value="">None</option><option value="grms">GRMS</option><option value="load">Load</option><option value="accessory">Accessory</option></select></div>
              <div><label class="label !mb-1" for="edit-rcu-category-{p.id}">RCU category</label><input id="edit-rcu-category-{p.id}" class="input !h-8 !text-xs" value={p.functional_category} onchange={(e) => saveField(p.id, 'functional_category', (e.currentTarget as HTMLInputElement).value)} /></div>
              <div><label class="label !mb-1" for="edit-rcu-type-{p.id}">RCU type</label><input id="edit-rcu-type-{p.id}" class="input !h-8 !text-xs" value={p.functional_type} onchange={(e) => saveField(p.id, 'functional_type', (e.currentTarget as HTMLInputElement).value)} /></div>
              <div class="flex flex-wrap items-end gap-3"><label class="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={p.syncs_to_rcu} onchange={(e) => saveField(p.id, 'syncs_to_rcu', (e.currentTarget as HTMLInputElement).checked)} /> Sync to RCU</label><label class="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={p.active} onchange={(e) => saveField(p.id, 'active', (e.currentTarget as HTMLInputElement).checked)} /> Active</label></div>
              <div><label class="label !mb-1" for="edit-price-{p.id}">List price</label><input id="edit-price-{p.id}" type="number" class="input !h-8 !text-xs" step="0.01" value={p.list_price_minor === null ? '' : minorToInputValue(p.list_price_minor)} onchange={(e) => saveField(p.id, 'list_price_minor', parseMoneyToMinor((e.currentTarget as HTMLInputElement).value))} /></div>
            </div></td></tr>
          {/if}
        {:else}
          <tr><td class="empty" colspan="6"><div class="py-8 text-center"><div class="font-medium text-navy">No products match this view</div><div class="mt-1 text-[12px] text-dark-gray">Try another family or clear the current filters.</div></div></td></tr>
        {/each}
      </tbody>
    </table>
  </div>

  {#if totalPages > 1}
    <div class="mt-3 flex items-center justify-center gap-3">
      <button type="button" class="btn-secondary !h-8 !w-8 !p-0" disabled={page <= 1} onclick={() => (page -= 1)} aria-label="Previous page">
        <ChevronLeft class="h-4 w-4" />
      </button>
      <span class="tym-small">Page {page} of {totalPages}</span>
      <button
        type="button"
        class="btn-secondary !h-8 !w-8 !p-0"
        disabled={page >= totalPages}
        onclick={() => (page += 1)}
        aria-label="Next page"
      >
        <ChevronRight class="h-4 w-4" />
      </button>
    </div>
  {/if}
{/if}

<style>
  /* Anchor sr-only header text inside the horizontal scrolling region. Without
     this, its absolute positioning can widen the document on narrow screens. */
  .tym-table-wrap {
    position: relative;
  }

  /* Scoped (not app.css) so this only touches the catalog product table's
     numeric column, not every other page reusing .num/.pq-num-input. */
  td.num {
    font-variant-numeric: tabular-nums;
  }
</style>
